import React, { useState } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Badge from '../components/common/Badge';
import Table from '../components/common/Table';
import { useAuth } from '../contexts/AuthContext';
import { FileText, Download, Eye } from 'lucide-react';
import { formatDate, delay } from '../utils/helpers';
import toast from 'react-hot-toast';

const ContratosListPage = () => {
  const { mockData } = useAuth();
  const navigate = useNavigate();

  const columns = [
    {
      header: 'Nº Contrato',
      cell: (row) => (
        <span className="font-mono text-sm font-semibold">{row.numeroContrato}</span>
      )
    },
    {
      header: 'Tipo',
      accessor: 'tipo'
    },
    {
      header: 'Cliente',
      accessor: 'partes.cliente'
    },
    {
      header: 'Fecha Inicio',
      cell: (row) => formatDate(row.fechaInicio)
    },
    {
      header: 'Fecha Fin',
      cell: (row) => formatDate(row.fechaFin)
    },
    {
      header: 'Estado',
      cell: (row) => (
        <Badge variant={row.estado === 'vigente' ? 'success' : 'default'}>
          {row.estado.toUpperCase()}
        </Badge>
      )
    },
    {
      header: 'Versiones',
      accessor: 'versiones'
    },
    {
      header: 'Acciones',
      cell: (row) => (
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            icon={Eye}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/contratos/${row.id}`);
            }}
          >
            Ver
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={Download}
            onClick={(e) => {
              e.stopPropagation();
              toast.success('Descargando PDF...');
            }}
          >
            PDF
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Contratos</h1>
        <p className="text-gray-600 mt-1">Gestión de contratos y anexos</p>
      </div>

      <Card>
        <Table
          columns={columns}
          data={mockData?.contratos || []}
          onRowClick={(row) => navigate(`/contratos/${row.id}`)}
        />
      </Card>
    </div>
  );
};

const ContratoDetallePage = () => {
  const { id } = useParams();
  const { mockData } = useAuth();
  const navigate = useNavigate();
  const [contrato, setContrato] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    const loadContrato = async () => {
      await delay(300);
      const found = mockData?.contratos?.find(c => c.id === id);
      setContrato(found);
      setLoading(false);
    };
    loadContrato();
  }, [id, mockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!contrato) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Contrato no encontrado</h2>
          <p className="text-gray-600 mb-6">No se pudo encontrar el contrato solicitado</p>
          <Button onClick={() => navigate('/contratos')}>
            Volver al listado
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Detalles del Contrato</h1>
          <p className="text-gray-600 mt-1">Información completa del contrato</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/contratos')}>
          Volver
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Información del Contrato">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Número de Contrato</p>
                <p className="font-semibold text-gray-900 font-mono">{contrato.numeroContrato}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Tipo</p>
                <p className="font-semibold text-gray-900">{contrato.tipo}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Estado</p>
                <Badge variant={contrato.estado === 'vigente' ? 'success' : 'default'}>
                  {contrato.estado.toUpperCase()}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-600">Versión</p>
                <p className="font-semibold text-gray-900">{contrato.versiones}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Fecha de Inicio</p>
                <p className="font-semibold text-gray-900">{formatDate(contrato.fechaInicio)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Fecha de Fin</p>
                <p className="font-semibold text-gray-900">{formatDate(contrato.fechaFin)}</p>
              </div>
            </div>
          </Card>

          <Card title="Partes del Contrato">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">Cliente</p>
                <p className="font-semibold text-gray-900">{contrato.partes.cliente}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-2">Proveedor</p>
                <p className="font-semibold text-gray-900">{contrato.partes.proveedor}</p>
              </div>
            </div>
          </Card>

          {contrato.clausulas && contrato.clausulas.length > 0 && (
            <Card title="Cláusulas">
              <div className="space-y-3">
                {contrato.clausulas.map((clausula, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">{clausula.titulo}</h4>
                    <p className="text-sm text-gray-700">{clausula.descripcion}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card title="Documentos">
            <div className="space-y-3">
              {contrato.documentos && contrato.documentos.length > 0 ? (
                contrato.documentos.map((doc, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-gray-600" />
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{doc.nombre}</p>
                        <p className="text-xs text-gray-500">{doc.tipo}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={Download}
                      onClick={() => toast.success('Descargando documento...')}
                    />
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No hay documentos adjuntos</p>
              )}
            </div>
          </Card>

          <Card title="Acciones">
            <div className="space-y-2">
              <Button
                variant="primary"
                className="w-full"
                icon={Download}
                onClick={() => toast.success('Descargando contrato...')}
              >
                Descargar PDF
              </Button>
              <Button
                variant="outline"
                className="w-full"
                icon={FileText}
                onClick={() => toast.success('Generando anexo...')}
              >
                Generar Anexo
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

const ContratosPage = () => {
  return (
    <Layout>
      <Routes>
        <Route index element={<ContratosListPage />} />
        <Route path=":id" element={<ContratoDetallePage />} />
      </Routes>
    </Layout>
  );
};

export default ContratosPage;
